// absolute imports
import queryString from 'query-string';

// Caches the org context resolved at login so it survives Auth0 redirects.
export const ORG_CONTEXT_STORAGE_KEY = 'ledgible_org_context';

// Set by cacheOrgContext; resets on full page load. Guards clearStaleOrgContext.
let writtenThisLoad = false;

// Accessing the sessionStorage property throws SecurityError when storage is
// denied (e.g. blocked cookies), so the access is wrapped. Returns null when
// unavailable.
const getStorage = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage || null;
  } catch (e) {
    return null;
  }
};

// query-string parses repeated params (?organization=a&organization=b) into an
// array; collapse to the first value so a malformed value can't flow downstream.
const firstValue = (v) => (Array.isArray(v) ? v[0] : v);

// Reads org context from the URL query (`organization`, `org_id` alias, `invitation`).
export const extractOrgFromQuery = (search) => {
  const parsed = queryString.parse(search || '');
  return {
    // eslint-disable-next-line camelcase
    organization: firstValue(parsed.organization || parsed.org_id) || undefined,
    invitation: firstValue(parsed.invitation) || undefined,
  };
};

// Reads org context cached in sessionStorage (persists across Auth0 redirects).
export const readCachedOrgContext = () => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(ORG_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

// Writes org context to sessionStorage; empty context clears the entry.
export const cacheOrgContext = (context) => {
  writtenThisLoad = true;
  const storage = getStorage();
  if (!storage) return;
  try {
    if (!context || (!context.organization && !context.invitation)) {
      storage.removeItem(ORG_CONTEXT_STORAGE_KEY);
      return;
    }
    storage.setItem(
      ORG_CONTEXT_STORAGE_KEY,
      JSON.stringify({
        organization: context.organization || undefined,
        invitation: context.invitation || undefined,
      }),
    );
  } catch (e) {
    // storage may be unavailable (private mode); fail silently
  }
};

export const clearCachedOrgContext = () => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(ORG_CONTEXT_STORAGE_KEY);
  } catch (e) {
    // storage may be unavailable (private mode); fail silently
  }
};

// Clears the cached context unless a login attempt wrote it this page load.
export const clearStaleOrgContext = () => {
  if (writtenThisLoad) return;
  clearCachedOrgContext();
};

export const resetOrgContextWriteTrackingForTests = () => {
  writtenThisLoad = false;
};

/**
 * Resolve org context in priority order: explicit opts > URL query > cached
 * sessionStorage. Returns source='none' if unset. Pass `opts.ignoreCache` to
 * skip the sessionStorage fallback (e.g. invite-token signups that must not
 * inherit a stale org).
 */
export const resolveOrgFromContext = (opts = {}) => {
  const location =
    opts.location || (typeof window !== 'undefined' ? window.location : null);

  if (opts.organization || opts.invitation) {
    return {
      organization: opts.organization || undefined,
      invitation: opts.invitation || undefined,
      source: 'explicit',
    };
  }

  if (location?.search) {
    const fromQuery = extractOrgFromQuery(location.search);
    if (fromQuery.organization || fromQuery.invitation) {
      return { ...fromQuery, source: 'query' };
    }
  }

  if (!opts.ignoreCache) {
    const cached = readCachedOrgContext();
    if (cached && (cached.organization || cached.invitation)) {
      return { ...cached, source: 'cache' };
    }
  }

  return {
    organization: undefined,
    invitation: undefined,
    source: 'none',
  };
};
